import React from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

const SwapConfirmationModal = ({ isOpen, onClose, swapData }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="relative bg-gray-800 rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-md mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                <CheckIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Swap Completed!
                </h3>
                <p className="text-sm text-white">
                  Your swap transaction was successful
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-white transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Swap Details */}
            <div className="bg-gray-700 rounded-lg p-4">
              <h4 className="text-sm font-medium text-white mb-3">Swap Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-white">You Sold:</span>
                  <span className="text-red-400">
                    {swapData?.from_amount?.toLocaleString()} {swapData?.from_currency}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white">You Received:</span>
                  <span className="text-white font-medium">
                    {swapData?.to_amount?.toFixed(6)} BTC1
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white">Digital Asset Marketplace Rate:</span>
                  <span className="text-white">
                    1 BTC1 = {1/swapData?.exchange_rate?.toFixed(6)} {swapData?.from_currency}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white">Fee ({swapData?.fee_percentage}%):</span>
                  <span className="text-white">
                    ${swapData?.fee_amount?.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white">Transaction ID:</span>
                  <span className="text-white font-mono text-xs">
                    {swapData?.transaction_id || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white">Timestamp:</span>
                  <span className="text-white text-xs">
                    {swapData?.timestamp ? new Date(swapData.timestamp).toLocaleString() : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Success Message */}
            <div className="bg-green-900 bg-opacity-50 border border-green-600 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
                <div>
                  <h4 className="text-green-300 font-medium mb-1">Transaction Successful</h4>
                  <p className="text-green-100 text-sm">
                    Your {swapData?.from_currency} has been successfully swapped for BTC1. 
                    The BTC1 has been credited to your wallet balance.
                  </p>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-blue-900 bg-opacity-50 border border-blue-600 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">i</span>
                </div>
                <div>
                  <h4 className="text-blue-300 font-medium mb-2">What's next?</h4>
                  <ul className="text-blue-100 text-sm space-y-1">
                    <li>• Your BTC1 is now available in your wallet</li>
                    <li>• You can use BTC1 for trading or other operations</li>
                    <li>• Check your wallet balance to see the updated amounts</li>
                    <li>• Transaction history is available in your account</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-gray-700 rounded-lg p-4">
              <h4 className="text-sm font-medium text-white mb-2">Need assistance?</h4>
              <p className="text-white text-sm">
                If you have any questions about this transaction, contact our support team at{' '}
                <a 
                  href="mailto:support@P2P1.com" 
                  className="text-white hover:text-white transition-colors"
                >
                  support@P2P1.com
                </a>
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-750 border-t border-gray-700">
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-3 text-sm font-semibold rounded-lg transition-all duration-200"
              >
                Close
              </button>
              <button
                onClick={() => {
                  // Navigate to wallet or refresh balances
                  window.location.reload();
                }}
                className="flex-1 bg-[#0E1696] hover:bg-[#0E1696] text-white py-3 text-sm font-semibold rounded-lg transition-all duration-200"
              >
                View Wallet
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SwapConfirmationModal;
